import { FirebaseError } from "firebase/app";
import Link from "next/link";
import { useRouter } from "next/router";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import Alert from "@components/Alert";
import { Button } from "@components/Button";
import { CenterAlignedTitle } from "@components/CenterAlignedTitle";
import { CommonHead } from "@components/CommonHead";
import CommonLayout from "@components/CommonLayout";
import LoginRequiredLayout from "@components/LoginRequiredLayout";
import type { PhoneNumberInputForm } from "@components/PhoneNumberInput";
import PhoneNumberInput from "@components/PhoneNumberInput";
import SocialAuthButtons from "@components/SocialAuthButtons";
import { Text } from "@components/Text";
import Toaster from "@components/Toaster";
import Auth from "@hooks/useAuth";
import { useRecaptchaVerifier } from "@hooks/useAuth/recaptchaVerifier";
import AuthResult from "@hooks/useAuthResult";
import ContinuePath from "@hooks/useContinuePath";
import useLogout from "@hooks/useLogout";
import Message, { PredefinedMessages } from "@hooks/useMessage";
import useOauthErrorHandler from "@hooks/useOauthErrorHandler";
import PhoneNumberBackup from "@hooks/usePhoneNumberBackup";
import { useRequestSmsVerification } from "@hooks/useRequestSmsVerification";
import { useSendCodeCooldown } from "@hooks/useSendCodeCooldown";
import { paths } from "@libs/paths";
import {
  signInWithPhoneNumber,
  translateToastErrorMessage,
} from "@libs/utils/firebase/auth";
import * as phoneNumberUtils from "@libs/utils/phone_number";
import { rollbar } from "@libs/utils/rollbar";
import styles from "./index.module.css";
import type { NextPage } from "next";

const Login: NextPage = () => {
  const router = useRouter();
  const {
    isCurrentUserReady,
    isFanstaUserReady,
    isFanstaUserRegisteredAndValidated,
    isOnlyFirebaseUserRegistered,
    signInWithGoogle,
    signInWithApple,
  } = Auth.useContainer();
  const { setConfirmationResult, confirmationResult } =
    AuthResult.useContainer();
  const { continuePath } = ContinuePath.useContainer();
  const logout = useLogout();
  const { addMessage } = Message.useContainer();
  const [isOauthAuthStarted, setIsOauthAuthStarted] = useState<boolean>(false);

  useEffect(() => {
    if (!isCurrentUserReady || !isFanstaUserReady) {
      return;
    }
    if (isFanstaUserRegisteredAndValidated) {
      // NOTE: OAuth認証はsignInWithPopupでページ遷移を伴わずこのページ上で完結するため、
      // 「今認証したのか」「既にログイン済みで訪れただけか」をフラグで判別している。
      // 依存配列には含めない(含めるとボタン押下時にeffectが再実行され、popup表示中にlogout()が走る)。
      if (isOauthAuthStarted) {
        addMessage(PredefinedMessages.loginWithAccountExists);
      }
      if (continuePath) {
        router.push(continuePath);
        return;
      }
      router.push(paths.home);
      return;
    }
    if (isOnlyFirebaseUserRegistered) {
      if (isOauthAuthStarted) {
        // Google/Appleでの本人確認はこの時点で完了し、有効なFirebase認証セッションが
        // 既に確立しているため、ログアウトさせずにそのまま基本情報入力へ進める。
        router.replace(paths.signupBasicInfo);
        return;
      }
      // このページでOAuth認証を行っていない場合は、以前のサインアップ中断等で
      // 残ったFirebaseセッションの可能性があるため、ログアウトして通常の
      // ログイン導線に留める。
      logout();
      return;
    }
    // 意図的な不要な操作に対応するため明示的にログアウトして、トークン情報等はクリアしておく。
    if (!confirmationResult) {
      logout();
    }
    // NOTE: router・logoutは毎レンダーで新しい参照になり得るため依存配列から除外している。
  }, [
    isCurrentUserReady,
    isFanstaUserReady,
    isFanstaUserRegisteredAndValidated,
    isOnlyFirebaseUserRegistered,
    confirmationResult,
    continuePath,
  ]);

  const handleOauthError = useOauthErrorHandler();

  const handleGoogleClick = useCallback(async (): Promise<void> => {
    // NOTE: 認証完了とほぼ同時に認証状態の変化がeffectへ伝わるため、awaitの前にフラグを立てる。
    setIsOauthAuthStarted(true);
    try {
      await signInWithGoogle();
    } catch (err: unknown) {
      // NOTE: キャンセル・失敗時はフラグを戻す(立てたままだと、後から別経路で
      // ログインが成立した際に身に覚えのないトーストが出るため)。
      setIsOauthAuthStarted(false);
      handleOauthError(err);
    }
  }, [signInWithGoogle, handleOauthError]);

  const handleAppleClick = useCallback(async (): Promise<void> => {
    setIsOauthAuthStarted(true);
    try {
      await signInWithApple();
    } catch (err: unknown) {
      setIsOauthAuthStarted(false);
      handleOauthError(err);
    }
  }, [signInWithApple, handleOauthError]);

  const { phoneNumberBackup, setPhoneNumberBackup } =
    PhoneNumberBackup.useContainer();

  const methods = useForm<PhoneNumberInputForm>({
    defaultValues: {
      phoneNumber: phoneNumberBackup.phoneNumber,
    },
    mode: "onBlur",
  });
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting },
  } = methods;

  const phoneNumber = useWatch({
    name: "phoneNumber",
    control,
  });

  const recaptchaVerifier = useRecaptchaVerifier();
  const { isCoolingDown, remainingSeconds, startCooldown } =
    useSendCodeCooldown(phoneNumber);
  const { requestSmsVerification } = useRequestSmsVerification();

  useEffect(() => {
    if (phoneNumber === undefined) {
      return;
    }
    setPhoneNumberBackup({
      ...phoneNumberBackup,
      phoneNumber: phoneNumber,
    });
  }, [phoneNumber]);

  const onSubmit = useCallback(
    async (input: PhoneNumberInputForm): Promise<void> => {
      if (!recaptchaVerifier || isCoolingDown) {
        return;
      }

      const firebasePhoneNumber = phoneNumberUtils.toFirebaseFormat(
        input.phoneNumber
      );

      // Backend rate-limit gate: block the send before reaching Firebase.
      const gate = await requestSmsVerification(firebasePhoneNumber);
      if (!gate.allowed) {
        addMessage(
          gate.reason === "invalidPhoneNumber"
            ? PredefinedMessages.commonError
            : PredefinedMessages.firebaseTooManyCodeRequests
        );
        return;
      }

      try {
        const result = await signInWithPhoneNumber(
          firebasePhoneNumber,
          recaptchaVerifier
        );

        setConfirmationResult(result);
        startCooldown();
        router.push(paths.loginAuth);
      } catch (err: unknown) {
        rollbar?.error(err instanceof Error ? err : new Error(String(err)));
        console.log("error", err);

        if (err instanceof FirebaseError) {
          addMessage(translateToastErrorMessage(err.code));
        }
      }
    },
    [
      recaptchaVerifier,
      setConfirmationResult,
      startCooldown,
      isCoolingDown,
      requestSmsVerification,
      addMessage,
    ]
  );

  const isButtonDisabled =
    !(phoneNumber?.length === 13 && isValid && !isSubmitting) || isCoolingDown;

  return (
    <CommonLayout noHeaderButton noModal>
      <CommonHead title="ログイン" ogType={"website"} />
      {isCurrentUserReady &&
        isFanstaUserReady &&
        !isFanstaUserRegisteredAndValidated && (
          <>
            <Toaster
              messageFilter={[
                PredefinedMessages.commonError,
                PredefinedMessages.firebaseTooManyCodeRequests,
                PredefinedMessages.emailConfirmationNotLoginError,
              ]}
            />

            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <LoginRequiredLayout>
                  <CenterAlignedTitle label="ログイン" />
                  <div className={styles.alert}>
                    <Alert>
                      ネット予約をご利用する場合は、電話番号登録が必要です。
                    </Alert>
                  </div>
                  <div className={styles.oauthButtons}>
                    <SocialAuthButtons
                      mode="login"
                      onGoogleClick={handleGoogleClick}
                      onAppleClick={handleAppleClick}
                    />
                  </div>
                  <div className={styles.divider}>
                    <span className={styles.dividerLine} />
                    または
                    <span className={styles.dividerLine} />
                  </div>
                  <PhoneNumberInput />
                  <Button
                    className={styles.btn}
                    styleType="filled"
                    colorType="primary"
                    disabled={isButtonDisabled}
                  >
                    {isCoolingDown
                      ? `再送信まで${remainingSeconds}秒`
                      : "ログイン"}
                  </Button>

                  <div className={styles.link}>
                    <Text size={14}>
                      アカウントをお持ちでない方
                      <Link href={paths.signup}>新規登録</Link>
                    </Text>
                  </div>
                </LoginRequiredLayout>
              </form>
            </FormProvider>
          </>
        )}
    </CommonLayout>
  );
};

export default Login;
